import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from './LoadingSpinner';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getTarotImage = (name: string) => {
  if (!name) return '';
  
  // Strip numbers and dots from the start (e.g. "0. The Fool" -> "The Fool")
  const cleanName = name.replace(/^\d+\.\s*/, '');

  const majorArcanaMap: { [key: string]: string } = {
    "The Fool": "00_the_fool.png",
    "The Magician": "01_the_magician.jpg",
    "The High Priestess": "02_the_high_priestess.png",
    "The Empress": "03_the_empress.jpg",
    "The Emperor": "04_the_emperor.jpg",
    "The Hierophant": "05_the_hierophant.jpg",
    "The Lovers": "06_the_lovers.jpg",
    "The Chariot": "07_the_chariot.jpg",
    "Strength": "08_strength.jpg",
    "The Hermit": "09_the_hermit.jpg",
    "Wheel of Fortune": "10_wheel_of_fortune.jpg",
    "Justice": "11_justice.jpg",
    "The Hanged Man": "12_the_hanged_man.jpg",
    "Death": "13_death.jpg",
    "Temperance": "14_temperance.jpg",
    "The Devil": "15_the_devil.jpg",
    "The Tower": "16_the_tower.jpg",
    "The Star": "17_the_star.jpg",
    "The Moon": "18_the_moon.jpg",
    "The Sun": "19_the_sun.jpg",
    "Judgement": "20_judgement.jpg",
    "The World": "21_the_world.jpg"
  };

  // Check Major Arcana first (case-insensitive check)
  const majorKey = Object.keys(majorArcanaMap).find(k => k.toLowerCase() === cleanName.toLowerCase().trim());
  if (majorKey) {
    return `${API_URL}/tarot_images/${majorArcanaMap[majorKey]}`;
  }

  // Minor Arcana (e.g., "Ace of Wands")
  const minorParts = cleanName.split(/ of /i);
  if (minorParts.length === 2) {
    const rank = minorParts[0].toLowerCase().trim();
    const suit = minorParts[1].toLowerCase().trim();
    
    const rankMap: { [key: string]: string } = {
      'ace': 'ace',
      'two': '02',
      'three': '03',
      'four': '04',
      'five': '05',
      'six': '06',
      'seven': '07',
      'eight': '08',
      'nine': '09',
      'ten': '10',
      'page': 'page',
      'knight': 'knight',
      'queen': 'queen',
      'king': 'king'
    };

    const finalRank = rankMap[rank] || rank;
    return `${API_URL}/tarot_images/${suit}_${finalRank}.jpg`;
  }

  return '';
};

const ReferenceLibrary = () => {
  const navigate = useNavigate();
  const [refs, setRefs] = useState([]);
  const [category, setCategory] = useState('tarot');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const sortTarot = (data: any[]) => {
    const majorArcanaOrder = [
      "The Fool", "The Magician", "The High Priestess", "The Empress", "The Emperor",
      "The Hierophant", "The Lovers", "The Chariot", "Strength", "The Hermit",
      "Wheel of Fortune", "Justice", "The Hanged Man", "Death", "Temperance",
      "The Devil", "The Tower", "The Star", "The Moon", "The Sun",
      "Judgement", "The World"
    ];

    const suitOrder = ["swords", "wands", "pentacles", "cups"];
    const rankOrder = ["ace", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "page", "knight", "queen", "king"];

    return [...data].sort((a, b) => {
      const cleanA = a.name.replace(/^\d+\.\s*/, '').trim().toLowerCase();
      const cleanB = b.name.replace(/^\d+\.\s*/, '').trim().toLowerCase();

      const majorIdxA = majorArcanaOrder.findIndex(k => k.toLowerCase() === cleanA);
      const majorIdxB = majorArcanaOrder.findIndex(k => k.toLowerCase() === cleanB);

      const isAMajor = majorIdxA !== -1;
      const isBMajor = majorIdxB !== -1;

      if (isAMajor && isBMajor) {
        return majorIdxA - majorIdxB;
      }
      if (isAMajor) return -1;
      if (isBMajor) return 1;

      // Minor Arcana logic: "Rank of Suit"
      const partsA = cleanA.split(' of ');
      const partsB = cleanB.split(' of ');

      if (partsA.length === 2 && partsB.length === 2) {
        const rankA = partsA[0];
        const suitA = partsA[1];
        const rankB = partsB[0];
        const suitB = partsB[1];

        if (suitA !== suitB) {
          return suitOrder.indexOf(suitA) - suitOrder.indexOf(suitB);
        }
        return rankOrder.indexOf(rankA) - rankOrder.indexOf(rankB);
      }

      return a.name.localeCompare(b.name);
    });
  };

  useEffect(() => {
    const fetchRefs = async () => {
      setLoading(true);
      try {
        const savedUser = localStorage.getItem('magic_user');
        const token = savedUser ? JSON.parse(savedUser).token : null;
        const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
        
        const res = await axios.get(`${API_URL}/references?category=${category}&search=${search}`, config);
        
        let sortedData = res.data;
        if (category === 'tarot' && !search) {
          sortedData = sortTarot(res.data);
        }
        
        setRefs(sortedData);
      } finally {
        setLoading(false);
      }
    };
    fetchRefs();
  }, [category, search]);

  const addToSpell = (e: React.MouseEvent, name: string) => {
    e.preventDefault();
    e.stopPropagation();
    const savedUser = localStorage.getItem('magic_user');
    if (!savedUser) {
      alert("Please login to add this to a spell.");
      return;
    }
    navigate(`/spells?prefill=${encodeURIComponent(name)}`);
  };

  return (
    <div className="content">
      <h2>Reference Library</h2>
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <input 
          type="text" 
          placeholder="Search by name, property, or association (e.g. 'Moon', 'Love', 'Protection')..." 
          value={search} 
          onChange={e => setSearch(e.target.value)}
          style={{ marginBottom: '0' }}
        />
      </div>
      <div className="tab-container">
        {['all', 'gem', 'herb', 'deity', 'tarot'].map(cat => (
          <div key={cat} className={`tab ${category === cat ? 'active' : ''}`} onClick={() => setCategory(cat)}>
            {cat.toUpperCase()}
          </div>
        ))}
      </div>
      {loading ? (
        <LoadingSpinner message="Consulting the ancient records..." />
      ) : (
        <div className={category === 'tarot' ? "tarot-grid" : "grid"}>
          {refs.map((ref: any) => (
            <Link key={ref.id} to={`/references/${ref.id}`} className={category === 'tarot' ? "tarot-card-item" : "card-link"}>
              {category === 'tarot' ? (
                <>
                  <div className="tarot-card-image-container">
                    <div className="protected-image-container">
                      <div className="image-overlay" onContextMenu={(e) => e.preventDefault()}></div>
                      <img 
                        src={getTarotImage(ref.name)} 
                        alt={ref.name} 
                        className="tarot-card-image no-save"
                        onError={(e: any) => { e.target.src = 'https://via.placeholder.com/300x520/1a0b2e/d4af37?text=Arcane+Circle'; }}
                      />
                    </div>
                  </div>
                  <div className="tarot-card-name">{ref.name}</div>
                </>
              ) : (
                <div className="card clickable-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <span className="badge">{ref.category}</span>
                    <button 
                      onClick={(e) => addToSpell(e, ref.name)}
                      style={{ padding: '0.2rem 0.5rem', fontSize: '0.7rem' }}
                    >
                      ✨ Add to Spell
                    </button>
                  </div>
                  <h3>{ref.name}</h3>
                  <p><strong>Description:</strong> {ref.description}</p>
                  <p><strong>Properties:</strong> {ref.properties}</p>
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReferenceLibrary;
